import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, Building2, Shield, FileCheck, Users, CheckCircle } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge } from '@/components/ui'
import { CTASection } from '@/components/sections'
import { industries } from '@/data/industries'

const complianceFrameworks = [
  { name: 'NCUA', description: 'National Credit Union Administration', forCU: true },
  { name: 'FFIEC', description: 'Federal Financial Institutions Examination Council', forCU: true, forBank: true },
  { name: 'GLBA', description: 'Gramm-Leach-Bliley Act', forCU: true, forBank: true },
  { name: 'PCI DSS', description: 'Payment Card Industry Data Security Standard', forCU: true, forBank: true },
  { name: 'SOC 2', description: 'Service Organization Control', forCU: true, forBank: true },
  { name: 'BSA/AML', description: 'Bank Secrecy Act / Anti-Money Laundering', forCU: true, forBank: true },
]

export function IndustriesPage() {
  return (
    <>
      <SEO
        title="Industries"
        description="UC Advisors specializes in technology solutions for Credit Unions and Banks, with deep expertise in financial services compliance and security."
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Badge variant="glow" className="mb-6">Financial Services Expertise</Badge>
            
            <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
              Built for{' '}
              <span className="text-gradient">Credit Unions & Banks</span>
            </h1>

            <p className="text-lg md:text-xl text-slate-300 mb-8">
              With 30+ years of combined experience serving financial institutions, 
              we understand the unique compliance requirements, security needs, and 
              member/customer expectations that define your industry.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Financial Services */}
      <section className="py-16 bg-primary-600/30">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Security-First',
                description: 'Financial data demands the highest security standards. Every solution we recommend is evaluated through a security lens first.',
              },
              {
                icon: FileCheck,
                title: 'Compliance-Ready',
                description: 'Our solutions help you meet NCUA, FFIEC, GLBA, and other regulatory requirements with built-in compliance features.',
              },
              {
                icon: Users,
                title: 'Member-Focused',
                description: 'Credit union members and bank customers expect modern digital experiences. We help you deliver without compromising security.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 text-center"
              >
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-accent-500/20 to-electric-500/20 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-accent-400" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-400">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Cards */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Industries We Serve</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Deep Expertise Where It Matters
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.id}
                initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Link to={industry.href}>
                  <div className="glass-card-hover rounded-3xl overflow-hidden h-full group">
                    {/* Header */}
                    <div className="p-8 pb-0">
                      <div className="flex items-start justify-between mb-6">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-500 to-electric-500 flex items-center justify-center shadow-glow">
                          <Building2 className="w-8 h-8 text-white" />
                        </div>
                        <Badge variant="secondary">{industry.shortTitle}</Badge>
                      </div>
                      <h3 className="text-2xl font-heading font-bold text-white mb-3 group-hover:text-accent-400 transition-colors">
                        {industry.title}
                      </h3>
                      <p className="text-slate-400 mb-6">{industry.description}</p>
                    </div>

                    {/* Stats */}
                    <div className="px-8 py-6 bg-white/5 grid grid-cols-2 gap-4">
                      {industry.stats.slice(0, 4).map((stat) => (
                        <div key={stat.label}>
                          <div className="text-2xl font-heading font-bold text-accent-400">
                            {stat.value}
                          </div>
                          <div className="text-sm text-slate-400">{stat.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="p-8 pt-6">
                      <div className="flex items-center gap-2 text-accent-400 font-medium group-hover:gap-3 transition-all">
                        Learn More
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Regulatory Compliance</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Solutions That Meet Your Requirements
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Every solution we recommend is evaluated against the regulatory frameworks 
              that govern your institution.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceFrameworks.map((framework, index) => (
              <motion.div
                key={framework.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="glass-card p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-500/20 flex items-center justify-center flex-shrink-0">
                    <FileCheck className="w-5 h-5 text-accent-400" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-white mb-1">
                      {framework.name}
                    </h4>
                    <p className="text-sm text-slate-400 mb-2">{framework.description}</p>
                    <div className="flex gap-2">
                      {framework.forCU && (
                        <span className="text-2xs px-2 py-0.5 rounded-full bg-accent-500/10 text-accent-400">
                          Credit Unions
                        </span>
                      )}
                      {framework.forBank && (
                        <span className="text-2xs px-2 py-0.5 rounded-full bg-electric-500/10 text-electric-400">
                          Banks
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core System Integrations */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4">Core Banking Integration</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-6">
                Works With Your Existing Systems
              </h2>
              <p className="text-lg text-slate-400 mb-8">
                Our solutions integrate with major core banking platforms. We understand 
                the technical requirements and have experience with the APIs and data 
                formats your systems use.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {['Symitar', 'Fiserv', 'Corelation', 'CU*Answers', 'DNA', 'Finastra', 'Jack Henry', 'FIS'].map((core) => (
                  <div key={core} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-accent-400" />
                    {core}
                  </div>
                ))}
              </div>

              <Link to="/contact">
                <Button variant="glow" className="group">
                  Discuss Your Integration Needs
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-3xl"
            >
              <h3 className="text-xl font-heading font-semibold text-white mb-6">
                Financial Services Specialization
              </h3>
              <div className="space-y-4">
                {[
                  'NCUA and FFIEC audit support',
                  'PCI DSS compliant contact center solutions',
                  'Encrypted communications for sensitive data',
                  'Branch-to-branch secure connectivity',
                  'Member/customer authentication integrations',
                  'Call recording and quality monitoring compliance',
                ].map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-success-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-success-500" />
                    </div>
                    <span className="text-slate-300 text-sm">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}

